import {
	BEST_OF_OPTIONS,
	type BestOf,
	canClaim,
	DIFFICULTIES,
	type Difficulty,
	gamesToWin,
	type MatchView,
	type PlayerIndex,
	REACTIONS,
	type Reaction,
	TURN_SECONDS_OPTIONS,
	type TurnSeconds,
} from "@playpal/core";
import { useEffect, useState } from "react";
import { play } from "../lib/sound";
import { Button, playerStyle } from "./ui";

// --- Series scoreboard ----------------------------------------------------

export function MatchScore({ match, seat }: { match: MatchView; seat: PlayerIndex }) {
	if (match.bestOf === 1) return null;
	const target = gamesToWin(match.bestOf);

	return (
		<div className="mb-3 flex items-center justify-center gap-3 text-sm font-bold">
			<span className="text-indigo-900/70 dark:text-indigo-100/70">
				Best of {match.bestOf} · game {match.gameNumber}
			</span>
			<span
				className="flex gap-1"
				role="img"
				aria-label={`Series ${match.wins[0]} to ${match.wins[1]}`}
			>
				{([0, 1] as const).map((index) => (
					<span key={index} className="flex gap-0.5">
						{Array.from({ length: target }, (_, pip) => (
							<span
								key={pip}
								className={`h-3 w-3 rounded-full ${
									pip < match.wins[index]
										? playerStyle(index).dot
										: "bg-indigo-900/15 dark:bg-white/20"
								}`}
							/>
						))}
					</span>
				))}
			</span>
			{match.winner !== null && (
				<span className="font-black">
					{match.winner === seat ? "You won the match! 🏆" : "Match lost 💪"}
				</span>
			)}
		</div>
	);
}

// --- Turn clock -----------------------------------------------------------

interface ClockProps {
	deadline: number | null;
	turnSeconds: TurnSeconds;
	clockOffset: number;
	yourTurn: boolean;
}

/** A calm ring, not a stressful buzzer — it only appears if switched on. */
export function TurnClock({ deadline, turnSeconds, clockOffset, yourTurn }: ClockProps) {
	const [now, setNow] = useState(() => Date.now() + clockOffset);

	useEffect(() => {
		if (deadline === null) return;
		// A fresh reading before the interval is armed: the mount-time value can
		// be minutes old by the time a deadline first appears, and a stale `now`
		// against a fresh deadline paints an absurd number of seconds until the
		// first tick corrects it.
		setNow(Date.now() + clockOffset);
		const timer = setInterval(() => setNow(Date.now() + clockOffset), 250);
		return () => clearInterval(timer);
	}, [deadline, clockOffset]);

	if (deadline === null || turnSeconds === 0) return null;

	const remaining = Math.max(0, deadline - now);
	const seconds = Math.ceil(remaining / 1000);
	const fraction = Math.min(1, remaining / (turnSeconds * 1000));
	const urgent = remaining < 6000;

	return (
		<div
			className="mx-auto mb-3 flex w-full max-w-xs items-center gap-3"
			role="timer"
			aria-label={`${seconds} seconds left this turn`}
		>
			<div className="h-2 flex-1 overflow-hidden rounded-full bg-white/60 dark:bg-white/10">
				<div
					className={`h-full rounded-full transition-[width] duration-200 ease-linear ${
						urgent ? "bg-rose-500" : "bg-emerald-500"
					}`}
					style={{ width: `${fraction * 100}%` }}
				/>
			</div>
			<span className={`w-14 text-right text-sm font-bold ${urgent ? "text-rose-600" : ""}`}>
				{seconds}s
			</span>
			{yourTurn && urgent && <span className="animate-wiggle text-lg">⏰</span>}
		</div>
	);
}

// --- Reactions ------------------------------------------------------------

export interface FloatingReaction {
	id: number;
	seat: PlayerIndex;
	emoji: Reaction;
}

export function ReactionBar({ onReact }: { onReact: (emoji: Reaction) => void }) {
	return (
		<div className="mt-6 flex flex-wrap justify-center gap-2">
			{REACTIONS.map((emoji) => (
				<button
					key={emoji}
					type="button"
					aria-label={`React with ${emoji}`}
					onClick={() => {
						play("tap");
						onReact(emoji);
					}}
					className="rounded-2xl bg-white/70 px-4 py-2 text-2xl shadow-md transition hover:scale-110 dark:bg-white/10"
				>
					{emoji}
				</button>
			))}
		</div>
	);
}

/** Reactions drift up the screen and vanish; nothing is ever kept. */
export function ReactionOverlay({ reactions }: { reactions: FloatingReaction[] }) {
	return (
		<div className="pointer-events-none fixed inset-x-0 bottom-24 flex justify-center gap-4">
			{reactions.map((reaction) => (
				<span key={reaction.id} className="animate-pop text-5xl drop-shadow-lg" aria-hidden="true">
					{reaction.emoji}
				</span>
			))}
		</div>
	);
}

// --- Match settings -------------------------------------------------------

interface SettingsProps {
	bestOf: BestOf;
	turnSeconds: TurnSeconds;
	onBestOf: (value: BestOf) => void;
	onTurnSeconds: (value: TurnSeconds) => void;
}

const TURN_LABEL: Record<TurnSeconds, string> = {
	0: "No clock",
	20: "20 seconds",
	45: "45 seconds",
};

export function MatchSettings({ bestOf, turnSeconds, onBestOf, onTurnSeconds }: SettingsProps) {
	return (
		<div className="mt-5 grid gap-4 sm:grid-cols-2">
			<fieldset>
				<legend className="mb-2 text-sm font-bold uppercase tracking-wide opacity-70">
					How many games?
				</legend>
				<div className="flex gap-2">
					{BEST_OF_OPTIONS.map((option) => (
						<button
							key={option}
							type="button"
							aria-pressed={bestOf === option}
							onClick={() => onBestOf(option)}
							className={`flex-1 rounded-2xl px-3 py-2 font-bold shadow-md transition ${
								bestOf === option
									? "bg-indigo-600 text-white"
									: "bg-white/70 hover:bg-white dark:bg-white/10"
							}`}
						>
							{option === 1 ? "Just one" : `Best of ${option}`}
						</button>
					))}
				</div>
			</fieldset>

			<fieldset>
				<legend className="mb-2 text-sm font-bold uppercase tracking-wide opacity-70">
					Turn clock
				</legend>
				<div className="flex gap-2">
					{TURN_SECONDS_OPTIONS.map((option) => (
						<button
							key={option}
							type="button"
							aria-pressed={turnSeconds === option}
							onClick={() => onTurnSeconds(option)}
							className={`flex-1 rounded-2xl px-3 py-2 font-bold shadow-md transition ${
								turnSeconds === option
									? "bg-indigo-600 text-white"
									: "bg-white/70 hover:bg-white dark:bg-white/10"
							}`}
						>
							{TURN_LABEL[option]}
						</button>
					))}
				</div>
			</fieldset>
		</div>
	);
}

interface AwayProps {
	/** When the offer opens, from the server. Null when there is nothing to claim. */
	claimableAt: number | null;
	friendName: string;
	clockOffset: number;
	onClaim: () => void;
}

/**
 * Shown to whoever is still here when the other player disappears mid-game.
 *
 * It waits before offering anything, because a dropped connection is far more
 * often a tunnel or a flat battery than someone storming off — and the moment
 * they reappear the server takes the offer back and this vanishes. Claiming is
 * a choice, never automatic: the point is that nobody is left staring at a
 * board that will not move again.
 */
export function FriendAway({ claimableAt, friendName, clockOffset, onClaim }: AwayProps) {
	const [now, setNow] = useState(() => Date.now() + clockOffset);

	useEffect(() => {
		if (claimableAt === null) return;
		// A fresh reading before the interval is armed — the mount-time value is
		// as old as the room screen, and a stale `now` here would open with
		// "Waiting 1245s" until the first tick corrected it.
		setNow(Date.now() + clockOffset);
		const timer = setInterval(() => setNow(Date.now() + clockOffset), 500);
		return () => clearInterval(timer);
	}, [claimableAt, clockOffset]);

	if (claimableAt === null) return null;

	const ready = canClaim(claimableAt, now);
	const seconds = Math.max(0, Math.ceil((claimableAt - now) / 1000));

	return (
		<div className="mb-4 rounded-2xl bg-amber-100 px-4 py-3 text-center dark:bg-amber-950/40">
			<p className="font-semibold text-amber-950 dark:text-amber-100">
				{friendName} has gone offline.
			</p>
			{ready ? (
				<>
					<p className="mt-1 text-sm text-amber-900/80 dark:text-amber-100/70">
						They haven't come back. You can take this one.
					</p>
					<Button className="mt-3" onClick={onClaim}>
						🏆 Take the win
					</Button>
				</>
			) : (
				<p className="mt-1 text-sm text-amber-900/80 dark:text-amber-100/70" role="timer">
					Waiting {seconds}s in case they come back…
				</p>
			)}
		</div>
	);
}

/**
 * How hard Robo plays, changeable mid-room.
 *
 * Only shown when there is a computer to change. It sits in the rail with the
 * players rather than behind the game picker, because the moment a child wants
 * it is the moment they are losing — not the moment they are choosing a game.
 */
export function DifficultyPicker({
	difficulty,
	onChange,
}: {
	difficulty: Difficulty;
	onChange: (difficulty: Difficulty) => void;
}) {
	return (
		<div className="rounded-2xl bg-white/60 px-3 py-2 dark:bg-white/10">
			<p className="mb-1.5 text-center text-sm font-bold opacity-70">🤖 Robo plays</p>
			<div className="grid grid-cols-3 gap-1.5">
				{DIFFICULTIES.map((level) => (
					<button
						key={level}
						type="button"
						aria-pressed={difficulty === level}
						onClick={() => {
							play("tap");
							onChange(level);
						}}
						className={`rounded-xl px-2 py-1.5 text-sm font-bold capitalize transition ${
							difficulty === level
								? "bg-indigo-600 text-white shadow"
								: "bg-white/70 hover:bg-white dark:bg-white/10"
						}`}
					>
						{level}
					</button>
				))}
			</div>
		</div>
	);
}
